import { Maybe } from '@aibees/shared/definition';
import { capitalize } from '@aibees/shared/utils';
import { sortBy } from 'lodash-es';
import { useMemo } from 'react';
import {
  companyTypeFilterLabelParser,
  personProfileBadgeFilterLabelParser,
} from '../feature/search-filters/components/filter-tags/utils';
import {
  CompanyFundingType,
  humanReadableFundingType,
} from '../feature/search-filters/config/funding-types';
import { COMPANY_TYPE } from '../feature/search-table-company';
import { KeywordSources } from '../feature/search-table-company/hooks/types';
import {
  PEOPLE_KEYWORD_SOURCES_CUSTOM_SORT_ORDER,
  PeopleKeywordSourceLabels,
  PeopleKeywordSources,
} from '../feature/search-table-people/hooks/types';
import { CompanySocialMedia } from '../types';
import { PersonProfileBadge } from '../types/person';
import { BrowseMetadataResponse, MetadataType } from './types';
import { useBrowseMetadata } from './use-browse-metadata';

export function useSearchQueryMetadata(
  metadataType: Maybe<MetadataType>,
  enabled: boolean
) {
  const api = useBrowseMetadata(
    { metadataType: metadataType ?? MetadataType.COMPANY_KEYWORD_SOURCE },
    { return: 'metadata' },
    enabled
  );

  const data = useMemo(() => {
    const mappedResult =
      mapMetadataResponse(
        metadataType ?? MetadataType.COMPANY_KEYWORD_SOURCE,
        api.data
      ) ?? [];

    return mappedResult.map((item) => ({
      ...item,
      value: item.key,
      count: item.value,
    }));
  }, [metadataType, api.data]);

  return useMemo(
    () => ({
      ...api,
      data,
    }),
    [api, data]
  );
}

// mapp the metadata response to key-value pairs, matching each filter config labels
// TODO: using hardcoded label values in this mapper, causes unintentional bugs in the future, we need to convert filter labels to enums in some filters at least
export function mapMetadataResponse(
  type: MetadataType,
  result?: BrowseMetadataResponse
) {
  if (type === MetadataType.COMPANY_KEYWORD_SOURCE) {
    const mapper = {
      [KeywordSources.NAME]: 'Company Name',
      [KeywordSources.KEYWORD]: 'Product & Services',
      [KeywordSources.SEO]: 'SEO Description',
      [KeywordSources.DESCRIPTION]: 'Company Description',
      [KeywordSources.INDUSTRY]: 'Industry',
    };
    const res = result?.map((item) => ({
      ...item,
      ...{ label: mapper[item.key as KeywordSources] },
    }));
    return sortBy(res, (item) => item.key);
  }

  if (type === MetadataType.PERSON_KEYWORD_SOURCE) {
    const mappedResult = result?.map((item) => ({
      ...item,
      label:
        PeopleKeywordSourceLabels[item.key as PeopleKeywordSources] || item.key,
    }));

    return sortBy(mappedResult, (item) =>
      PEOPLE_KEYWORD_SOURCES_CUSTOM_SORT_ORDER.indexOf(
        item.key as PeopleKeywordSources
      )
    );
  }

  if (
    type === MetadataType.COMPANY_SOCIAL_MEDIA ||
    type === MetadataType.PERSON_SOCIAL_MEDIA
  ) {
    const res = result?.map((item) => ({
      ...item,
      ...{
        label: capitalize(item.key === 'TWITTER' ? 'X (Twitter)' : item.key),
      },
    }));
    return sortBy(res, (item) => sortSocialMediaKeys(item.key));
  }

  if (type === MetadataType.PERSON_PROFILE_BADGE) {
    const res = result?.map((item) => ({
      ...item,
      ...{
        label: personProfileBadgeFilterLabelParser(
          item.key,
          item.key as PersonProfileBadge
        ),
      },
    }));
    return sortBy(res, (item) => sortProfileBadgeKeys(item.key));
  }

  if (type === MetadataType.COMPANY_TYPE) {
    const res = result?.map((item) => ({
      ...item,
      ...{ label: companyTypeFilterLabelParser(item.key, item.key) },
    }));
    return sortBy(res, (item) => sortCompanyTypeKeys(item.key as COMPANY_TYPE));
  }

  if (type === MetadataType.COMPANY_FUNDING_TYPE) {
    const res = result?.map((item) => ({
      ...item,
      ...{ label: humanReadableFundingType(item.key as CompanyFundingType) },
    }));
    return sortBy(res, (item) => item.key);
  }

  if (type === MetadataType.COMPANY_EMPLOYEE_SIZE) {
    const res = result?.map((item) => ({
      ...item,
      ...{ label: item.key },
    }));
    return sortBy(res, (item) => Number(item.key.split('-')[0]));
  }

  if (type === MetadataType.COMPANY_REVENUE) {
    const res = result?.map((item) => ({
      ...item,
      ...{ label: item.key },
    }));
    return res;
  }

  if (type === MetadataType.PERSON_SENIORITY) {
    const res = result?.map((item) => ({
      ...item,
      ...{ key: item.key === 'MID_LEVEL' ? 'MID-LEVEL' : item.key },
    }));
    return res;
  }

  return result;
}

function sortProfileBadgeKeys(key: string) {
  switch (key) {
    case PersonProfileBadge.PREMIUM:
      return 0;
    case PersonProfileBadge.OPEN_TO_WORK:
      return 1;
    case PersonProfileBadge.INFLUENCER:
      return 2;
    case PersonProfileBadge.CREATOR:
      return 3;
    case PersonProfileBadge.HIRING:
      return 4;
  }
}

function sortSocialMediaKeys(key: string) {
  switch (key) {
    case CompanySocialMedia.LINKEDIN:
      return 0;
    case CompanySocialMedia.FACEBOOK:
      return 1;
    case CompanySocialMedia.TWITTER:
      return 2;
    case CompanySocialMedia.INSTAGRAM:
      return 3;
    case 'GITHUB':
      return 4;
  }
}

function sortCompanyTypeKeys(key: COMPANY_TYPE) {
  switch (key) {
    case COMPANY_TYPE.PRIVATELY_HELD:
      return 0;
    case COMPANY_TYPE.SELF_OWNED:
      return 1;
    case COMPANY_TYPE.SELF_EMPLOYED:
      return 2;
    case COMPANY_TYPE.PARTNERSHIP:
      return 3;
    case COMPANY_TYPE.PUBLIC_COMPANY:
      return 4;
    case COMPANY_TYPE.NON_PROFIT:
      return 5;
    case COMPANY_TYPE.EDUCATIONAL:
      return 6;
    case COMPANY_TYPE.GOVERNMENT_AGENCY:
      return 7;
  }
}
